import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { connecter } from '../api/client.ts';
import { useSession } from '../composants/session.tsx';
import { Erreur } from '../composants/communs.tsx';
import { Icone } from '../composants/icones.tsx';

const VERSION = '0.1.0';

const FONCTIONNALITES = [
  'Ventes, stock et crédit clients',
  'Réceptions, transferts et inventaires',
  'Facturation, boutiques et rapports',
];

/**
 * Connexion — RG-163, RG-164.
 *
 * Un seul champ secret : l'administrateur y tape son MOT DE PASSE, le
 * vendeur son code PIN. C'est le serveur qui impose le mot de passe à
 * l'administrateur ; l'écran ne fait que transmettre.
 *
 * L'écran ne distingue JAMAIS « identifiant inconnu » de « code faux » : le
 * message vient du serveur, qui reste volontairement vague. Dire lequel des
 * deux est correct offrirait un moyen d'énumérer les comptes.
 */
export function Connexion(): ReactNode {
  const { session } = useSession();
  const naviguer = useNavigate();

  const [identifiant, setIdentifiant] = useState('');
  const [secret, setSecret] = useState('');
  const [secretVisible, setSecretVisible] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);

  if (session !== null) {
    return <Navigate to="/" replace />;
  }

  async function soumettre(evenement: SyntheticEvent): Promise<void> {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await connecter(identifiant.trim(), secret);
      await naviguer('/');
    } catch (echec) {
      setErreur(echec);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="connexion">
      {/*
        Deux panneaux : l'identité de marque à gauche, le formulaire à
        droite. L'œil arrive sur le nom de l'outil et ce qu'il fait, avant de
        descendre sur les champs — une carte seule au milieu d'un fond vide
        ne disait ni l'un ni l'autre.
      */}
      <aside className="connexion__marque">
        <div className="connexion__logo-marque" aria-hidden="true">
          <span className="connexion__bande" />
          <span className="connexion__bande" />
          <span className="connexion__bande" />
        </div>
        <p className="connexion__nom">
          PISCHON <strong>SOFT</strong>
        </p>
        <p className="connexion__accroche">
          La gestion commerciale multi-boutique, tout-en-un.
        </p>
        <span className="connexion__trait" aria-hidden="true" />

        <ul className="connexion__liste">
          {FONCTIONNALITES.map((fonctionnalite) => (
            <li key={fonctionnalite}>{fonctionnalite}</li>
          ))}
        </ul>
      </aside>

      <div className="connexion__panneau">
        <form
          className="connexion__formulaire"
          onSubmit={(evenement) => {
            void soumettre(evenement);
          }}
        >
          <h1>Connexion</h1>
          <p className="connexion__sous-titre">Accédez à votre espace de travail.</p>

          <div className="champ champ--icone">
            <label htmlFor="identifiant">Identifiant</label>
            <div className="champ__saisie">
              <Icone nom="personne" taille={18} />
              <input
                id="identifiant"
                name="identifiant"
                autoComplete="username"
                autoFocus
                placeholder="Nom d’utilisateur"
                value={identifiant}
                onChange={(evenement) => {
                  setIdentifiant(evenement.target.value);
                }}
              />
            </div>
          </div>

          <div className="champ champ--icone">
            <label htmlFor="secret">Mot de passe ou code PIN</label>
            {/* Un seul champ secret : le serveur sait lequel des deux
                attendre (RG-163, RG-164). */}
            <div className="champ__saisie">
              <Icone nom="cadenas" taille={18} />
              <input
                id="secret"
                name="secret"
                type={secretVisible ? 'text' : 'password'}
                autoComplete="current-password"
                /* Plafond identique à celui du serveur (128). Aucun filtrage
                   des caractères : un mot de passe en contient de toutes
                   sortes. */
                maxLength={128}
                placeholder="Mot de passe ou code PIN"
                value={secret}
                onChange={(evenement) => {
                  setSecret(evenement.target.value);
                }}
              />
              <button
                type="button"
                className="champ__oeil"
                aria-label={secretVisible ? 'Masquer le champ' : 'Afficher le champ'}
                aria-pressed={secretVisible}
                onClick={() => {
                  setSecretVisible((visible) => !visible);
                }}
              >
                <Icone nom="oeil" taille={18} />
              </button>
            </div>
          </div>

          <Erreur erreur={erreur} />

          <button
            type="submit"
            className="connexion__bouton"
            disabled={enCours}
            aria-busy={enCours}
          >
            {enCours ? 'Connexion…' : 'Se connecter'}
          </button>

          <hr className="connexion__separateur" />

          <p className="connexion__version">
            Version {VERSION} · © 2026 Pischon Group — Tous droits réservés
          </p>
        </form>
      </div>
    </div>
  );
}
